import { HostState } from './host-state.js'
import { EntryCode } from './entry-code.js'
import { CLASS_PLACEMENT_KEYS, ClassPlaceCount, ShowPlaceCount } from './place-count.js'
import { z } from 'zod'
export * from './model-reconciliation.js'
export * from './person-contact.js'
export * from './extracted-honey-colour.js'
export * from './place-count.js'
import { ShowSlug, Slug } from './slug.js'
import { SeriesAnnualProjection } from './series-report.js'

export const RubricCriterion = z.object({
  name: z.string(),
  maxPoints: z.number().int().positive(),
  weight: z.number().positive().default(1),
})

export const Organization = z.object({
  id: z.guid().optional(),
  name: z.string(),
  contact: z.string().nullable().optional(),
  abbreviation: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const CreateOrganizationRequest = z.object({
  name: z.string(),
  contact: z.string().optional(),
  abbreviation: z.string().optional(),
  website: z.string().optional(),
})

/** App-created clubs use CUIDs; clubs created by a reviewed show import use UUIDs. */
export const ClubId = z.union([z.string().cuid(), z.guid()])

export const Club = z.object({
  id: ClubId.optional(),
  name: z.string(),
  organizationId: z.guid().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const CreateClubRequest = z.object({
  name: z.string().min(1),
  organizationId: z.guid().nullable().optional(),
})

export const UpdateClubRequest = z.object({
  name: z.string().min(1).optional(),
  organizationId: z.guid().nullable().optional(),
})

export const BulkCreateClubsRequest = z.object({
  names: z.array(z.string().min(1)).min(1),
})

export const BulkCreateClubsResponse = z.object({
  created: z.array(Club),
  skipped: z
    .array(
      z.object({
        name: z.string(),
        reason: z.enum(['duplicate']),
      })
    )
    .optional(),
})

export const ListClubsResponse = z.object({
  clubs: z.array(Club),
})

export const ShowStatus = z.enum(['draft', 'accepting_entries', 'judging', 'finalized'])

const AwardEligibilityConfig = z.object({
  staffEligible: z.boolean().optional(),
})

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

const ShowDateString = z
  .string()
  .refine(value => DATE_ONLY_REGEX.test(value) || !Number.isNaN(Date.parse(value)), {
    message: 'Invalid datetime',
  })

export const AwardsPolicy = z
  .object({
    awardEligibility: z.record(z.string(), AwardEligibilityConfig).optional(),
    bestInDivisionEnabled: z.boolean().optional(),
    heroAwardEnabled: z.boolean().optional(),
    /** Awarded only while the show tracks clubs. */
    clubSpiritAwardEnabled: z.boolean().optional(),
    clubChampionAwardEnabled: z.boolean().optional(),
  })
  .partial({
    bestInDivisionEnabled: true,
  })

export const SpecialAwardType = z.enum(['best_in_show', 'grand_champion', 'reserve_grand'])

export const SpecialAwardRecommendation = z.object({
  type: SpecialAwardType,
  staffEligible: z.boolean(),
  winnerExhibitorId: z.guid().nullable(),
  skippedStaffExhibitorIds: z.array(z.guid()),
})

export const ShowSeries = z.object({
  isChampionship: z.boolean().default(false),
  id: z.guid(),
  organizationId: z.guid(),
  name: z.string().trim().min(1).max(200),
  slug: Slug,
  metadataRevision: z.number().int().nonnegative(),
})
export const SeriesMembership = z.object({
  series: ShowSeries.nullable(),
  year: z.number().int().nullable(),
  revision: z.number().int().nonnegative(),
  canEdit: z.boolean(),
})
export const CreateShowSeriesRequest = ShowSeries.pick({
  organizationId: true,
  name: true,
  slug: true,
  isChampionship: true,
}).strict()
export const UpdateShowSeriesRequest = z
  .object({
    isChampionship: z.boolean().optional(),
    name: z.string().trim().min(1).max(200),
    expectedMetadataRevision: z.number().int().nonnegative(),
  })
  .strict()
export const ShowSeriesResponse = z.object({ series: ShowSeries })
export const ListShowSeriesResponse = z.object({
  series: z.array(ShowSeries),
  nextCursor: z.string().nullable(),
})
export const SeriesEditionSummary = z.object({
  showId: z.guid(),
  name: z.string(),
  slug: z.string(),
  year: z.number().int(),
  status: ShowStatus,
  membershipRevision: z.number().int(),
  canEdit: z.boolean(),
})
export const ListSeriesEditionsResponse = z.object({
  editions: z.array(SeriesEditionSummary),
  nextCursor: z.string().nullable(),
})
export type TShowSeries = z.infer<typeof ShowSeries>
export type TSeriesMembership = z.infer<typeof SeriesMembership>
export type CreateShowSeriesInput = z.input<typeof CreateShowSeriesRequest>
export type UpdateShowSeriesInput = z.infer<typeof UpdateShowSeriesRequest>
export type ListShowSeriesOutput = z.infer<typeof ListShowSeriesResponse>
export type ListSeriesEditionsOutput = z.infer<typeof ListSeriesEditionsResponse>

export const Show = z.object({
  id: z.guid().optional(),
  orgId: z.guid(),
  seriesMembership: SeriesMembership.optional(),
  name: z.string(),
  slug: ShowSlug,
  startDate: ShowDateString,
  endDate: ShowDateString,
  location: z.string().optional(),
  hostState: HostState.nullish(),
  basicDetailsPublished: z.boolean().optional(),
  status: ShowStatus.default('draft'),
  rulesVersion: z.string().optional(),
  pointsConfig: z.string().optional(),
  exhibitCounter: z.number().int().optional(),
  exhibitPermMod: z.number().int().optional(),
  exhibitPermA: z.number().int().optional(),
  exhibitPermB: z.number().int().optional(),
  awardsPolicy: AwardsPolicy.nullish(),
  enableClubs: z.boolean().default(true),
  labelOnlyMode: z.boolean().optional(),
  labelPrintingDisabled: z.boolean().optional(),
  staffMayExhibit: z.boolean().optional(),
  allowStaffEntries: z.boolean().optional(),
  reserveGrandEnabled: z.boolean().optional(),
  defaultPlaceCount: ShowPlaceCount.optional(),
  judgingFinalizedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const AppliedRuleSnapshotStatus = z.enum(['approved', 'pending_approval', 'superseded'])

export const RuleSnapshotPayload = z.record(z.string(), z.unknown())

export const AppliedRuleSnapshot = z.object({
  id: z.guid().optional(),
  showId: z.guid(),
  sourceSystem: z.string(),
  sourceType: z.string().nullable().optional(),
  sourceId: z.string().nullable().optional(),
  sourceChecksum: z.string(),
  rulesVersion: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  documentUrl: z.string().url().nullable().optional(),
  docxUrl: z.string().url().nullable().optional(),
  snapshot: RuleSnapshotPayload,
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  status: AppliedRuleSnapshotStatus,
  approvedBy: z.string().nullable().optional(),
  approvedAt: z.string().datetime().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const UpsertAppliedRuleSnapshotRequest = z.object({
  sourceSystem: z.string().min(1).default('manual'),
  sourceType: z.string().nullable().optional(),
  sourceId: z.string().nullable().optional(),
  rulesVersion: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  documentUrl: z.string().url().nullable().optional(),
  docxUrl: z.string().url().nullable().optional(),
  snapshot: RuleSnapshotPayload,
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
})

export const AppliedRuleSnapshotResponse = z.object({
  snapshot: AppliedRuleSnapshot.nullable(),
})

export const ApproveAppliedRuleSnapshotRequest = z.object({
  snapshotId: z.guid(),
})

export const CreateShowRequest = Show.pick({
  orgId: true,
  name: true,
  startDate: true,
  endDate: true,
  location: true,
  hostState: true,
  rulesVersion: true,
}).extend({
  seriesId: z.guid().nullable().optional(),
  slug: ShowSlug.optional(),
  awardsPolicy: AwardsPolicy.nullish().optional(),
  enableClubs: z.boolean().optional(),
  labelOnlyMode: z.boolean().optional(),
  labelPrintingDisabled: z.boolean().optional(),
  staffMayExhibit: z.boolean().optional(),
  allowStaffEntries: z.boolean().optional(),
  reserveGrandEnabled: z.boolean().optional(),
})

export const OrganizationWithShows = Organization.extend({
  shows: z.array(Show),
})

export const LeaderboardEntry = z.object({
  exhibitorId: z.guid(),
  points: z.number(),
  placements: z.record(z.string(), z.number().int()),
  entriesCount: z.number().int(),
  rank: z.number().int(),
})

export const ChampionTieGroup = z.object({
  awardType: z.enum(['grand_champion', 'reserve_grand']).optional(),
  rank: z.number().int(),
  exhibitorIds: z.array(z.guid()).min(2),
})

export const ChampionLeaders = z.object({
  champion: LeaderboardEntry.nullable(),
  reserve: LeaderboardEntry.nullable(),
  tieGroups: z.array(ChampionTieGroup),
})

export const LeaderboardResponse = z.object({
  leaderboard: z.array(LeaderboardEntry),
  championLeaders: ChampionLeaders,
  reserveEligibleLeaderboard: z.array(LeaderboardEntry).optional(),
  specialAwards: z.array(SpecialAwardRecommendation).optional(),
  exhibitorNames: z.record(z.guid(), z.string()),
})

export const CatalogCategory = z.object({
  code: z.string(),
  name: z.string(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const CatalogRules = z.union([z.string(), z.record(z.string(), z.unknown())])

export const CatalogFlags = z.union([z.string(), z.record(z.string(), z.unknown())])

const FlexibleStringArray = z.union([z.string(), z.array(z.string())])

export const ENTRY_CODE_ALPHABET = '0123456789' as const

export { EntryCode, historicalIdentifierOrder } from './entry-code.js'
export { isNonAdjacentIdentifierOrder } from './identifier-order.js'

// Historical imports may retain original codes; intake only issues 1000–9999.
export const IntakeEntryCode = z
  .string()
  .length(4)
  .regex(/^[1-9][0-9]{3}$/u, 'Exhibit ID must be a four-digit number (1000–9999)')

export const IdentifierSummary = z.object({
  value: IntakeEntryCode,
  orderIndex: z.number().int().nonnegative(),
})

export const ReportSummaryResponse = z.object({
  showId: z.guid(),
  generatedAt: z.string().datetime(),
  entrants: z.number().int().nonnegative(),
  entries: z.number().int().nonnegative(),
  blackJarEntries: z.number().int().nonnegative(),
})

export const ReportClubParticipationRow = z.object({
  clubId: ClubId.nullable(),
  clubName: z.string(),
  exhibitorCount: z.number().int().nonnegative(),
  entryCount: z.number().int().nonnegative(),
  totalPoints: z.number().nonnegative(),
  percentOfEntrants: z.number().nonnegative(),
  percentOfEntries: z.number().nonnegative(),
})

// Auth
export const AuthLoginRequest = z.object({
  email: z.string().email(),
  password: z.string().min(1),
})

export const AuthUser = z.object({
  personId: z.guid().nullable().optional(),
  /** The linked person, whose show assignments give the account its show roles. */
  person: z.object({ id: z.guid(), name: z.string() }).nullable().optional(),
  showRoles: z
    .array(z.object({ showId: z.guid(), role: z.string(), showName: z.string().optional() }))
    .optional(),
  id: z.guid(),
  email: z.string().email(),
  isAdmin: z.boolean(),
})

export const AuthUserResponse = z.object({
  user: AuthUser,
})

/**
 * Where setup and reset links open; the token follows in the URL fragment, never the query.
 * Not `/account/setup`, which would shadow the Setup page of a show with the slug `account`.
 */
export const ACCESS_LINK_PATH = '/account/access'

/** `invited`: created without a password and not yet set up through its link. */
export const AccountStatus = z.enum(['active', 'invited', 'disabled'])
/** `setup` sets an invited account's first password; `reset` replaces an existing one. */
export const AccessLinkKind = z.enum(['setup', 'reset'])

export const AccessLinkInspectRequest = z.object({ token: z.string().min(1).max(128) })
export const AccessLinkInspectResponse = z.object({
  kind: AccessLinkKind,
  email: z.string().email(),
  expiresAt: z.string().datetime(),
})
export const AccessLinkCompleteRequest = z.object({
  token: z.string().min(1).max(128),
  newPassword: z.string().min(1),
})

/** What the sign-in pages may offer on this server. */
export const AuthOptionsResponse = z.object({
  /** The server can email setup and reset links, so Forgot password is offered. */
  accountEmail: z.boolean(),
})
/** Always answered 202, whether or not an account uses the email. */
export const ForgotPasswordRequest = z.object({ email: z.string().trim().email().max(320) })
/** The link an administrator was shown; it must still be the account's live link. */
export const AccessLinkEmailRequest = z.object({ token: z.string().min(1).max(128) })
export const AccessLinkEmailResponse = z.object({ sentTo: z.string() })

/** Returned once, when issued; only its hash is stored. */
export const IssuedAccessLink = z.object({
  token: z.string(),
  kind: AccessLinkKind,
  expiresAt: z.string().datetime(),
})

export const AdminUser = AuthUser.extend({
  personId: z.guid().nullable(),
  person: z.object({ id: z.guid(), name: z.string(), email: z.string().nullable() }).nullable(),
  status: AccountStatus,
  /** False until the person sets a password from a setup link; stays known while disabled. */
  hasPassword: z.boolean(),
  disabledAt: z.string().datetime().nullable(),
  lastLoginAt: z.string().datetime().nullable(),
  /** Expiry of the outstanding setup or reset link, which may already have passed. */
  accessLinkExpiresAt: z.string().datetime().nullable(),
  /** Users list only: the number of shows the linked person is staff in. */
  showCount: z.number().int().min(0).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

/** Invites an account: the person sets their own password through the returned setup link. */
export const CreateAdminUserRequest = z.object({
  email: z.string().email(),
  isAdmin: z.boolean().optional(),
  personId: z.guid().nullable().optional(),
})

export const SetAdminUserPersonRequest = z.object({ personId: z.guid().nullable() })
export const SetAdminUserPersonResponse = z.object({ user: AdminUser })

export const CreateAdminUserResponse = z.object({
  user: AdminUser,
  accessLink: IssuedAccessLink,
})

/** Send at least one field. */
export const UpdateAdminUserRequest = z.object({
  email: z.string().email().optional(),
  isAdmin: z.boolean().optional(),
})
export const AdminUserResponse = z.object({ user: AdminUser })

/** Why an account has show access: its linked person's staff assignments. */
export const AdminUserShowAccess = z.object({
  assignmentId: z.guid(),
  role: z.string(),
  show: z.object({ id: z.guid(), name: z.string(), slug: z.string(), status: z.string() }),
})
/** Account changes the server records; `user.create` and `user.password.set` are history only. */
export const ACCOUNT_AUDIT_ACTIONS = [
  'user.invite',
  'user.create',
  'user.setup.complete',
  'user.password.reset',
  'user.password.change',
  'user.password.set',
  'user.setup_link.issue',
  'user.reset_link.issue',
  'user.setup_link.email',
  'user.reset_link.email',
  'user.setup_link.request',
  'user.reset_link.request',
  'user.email.change',
  'user.admin.grant',
  'user.admin.revoke',
  'user.disable',
  'user.enable',
  'user.sessions.revoke',
  'user.person.link',
  'user.person.unlink',
  'user.invite.revoke',
] as const
/** Staff changes to the account's person, which account history also lists. */
export const STAFF_AUDIT_ACTIONS = [
  'show.staff.create',
  'show.staff.update',
  'show.staff.delete',
] as const

export const AdminUserHistoryEvent = z.object({
  id: z.guid(),
  /** One of ACCOUNT_AUDIT_ACTIONS or STAFF_AUDIT_ACTIONS. */
  action: z.string(),
  createdAt: z.string().datetime(),
  /** The acting person's name, account email, "Command line", or "Unknown". */
  actor: z.string(),
  /** For a staff change, the role and show, such as "Secretary in Spring Show". */
  detail: z.string().nullable(),
})
export const AdminUserDetailResponse = z.object({
  user: AdminUser,
  showAccess: z.array(AdminUserShowAccess),
  /** The newest 20 changes to the account or its person's staff roles, newest first. */
  history: z.array(AdminUserHistoryEvent),
  /** Shows this installation operates, where a staff role can be added. */
  assignableShows: z.array(z.object({ id: z.guid(), name: z.string() })),
})
export const IssueAccessLinkResponse = z.object({ user: AdminUser, accessLink: IssuedAccessLink })

export const ListAdminUsersResponse = z.object({
  users: z.array(AdminUser),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
})

export const ChangePasswordRequest = z.object({
  currentPassword: z.string().min(1),
  // The server's policy also refuses common passwords and more than 72 UTF-8 bytes.
  newPassword: z.string().min(12).max(64),
})

/** A configured placement value can make a club's points negative. */
export const ReportClubStanding = z.object({
  clubName: z.string(),
  entries: z.number().int().nonnegative(),
  points: z.number(),
  exhibitors: z.array(
    z.object({
      exhibitorId: z.guid(),
      exhibitorName: z.string().nullable(),
      points: z.number(),
    })
  ),
})

/** The club awards in report order, with the total each ranks clubs by. */
export const CLUB_AWARDS = [
  { key: 'clubSpirit', title: 'Club Spirit Award', basis: 'Most entries', measure: 'entries' },
  { key: 'clubChampion', title: 'Club Champion Award', basis: 'Most points', measure: 'points' },
] as const
export type ClubAwardKey = (typeof CLUB_AWARDS)[number]['key']

/**
 * Club Spirit goes to the most entries and Club Champion to the most points. Equal totals share
 * a place; standings are reported whether or not the show gives the award.
 */
export const ReportClubAward = z.object({
  enabled: z.boolean(),
  winners: z.array(ReportClubStanding),
  runnersUp: z.array(ReportClubStanding),
})

/** A placed club's total as reports print it: entries for Club Spirit, points for Club Champion. */
export function formatClubAwardTotal(
  club: { entries: number; points: number },
  measure: 'entries' | 'points'
): string {
  return measure === 'entries'
    ? `${club.entries} ${club.entries === 1 ? 'entry' : 'entries'}`
    : `${club.points} ${club.points === 1 ? 'pt' : 'pts'}`
}

export function formatClubAwardStanding(
  club: { clubName: string; entries: number; points: number },
  measure: 'entries' | 'points'
): string {
  return `${club.clubName} (${formatClubAwardTotal(club, measure)})`
}

export const ReportClubParticipationResponse = z.object({
  showId: z.guid(),
  enableClubs: z.boolean(),
  generatedAt: z.string().datetime(),
  totals: z.object({
    entrants: z.number().int().nonnegative(),
    entries: z.number().int().nonnegative(),
  }),
  rows: z.array(ReportClubParticipationRow),
  clubSpiritAward: ReportClubAward,
  clubChampionAward: ReportClubAward,
})

export const ReportPlacementEntry = z.object({
  exhibitId: EntryCode,
  exhibitorId: z.guid().nullable(),
  exhibitorName: z.string().nullable(),
  entryTitle: z.string().nullable(),
})

export const ReportClassPlacements = z.record(z.string(), z.array(ReportPlacementEntry))

export const ReportClassResults = z.object({
  classId: z.guid(),
  classCode: z.string().nullable(),
  className: z.string(),
  classOrder: z.number().int().nullable(),
  entriesCount: z.number().int().nonnegative().optional(),
  /** Places the class awards; saved results may hold more. */
  placeCount: ClassPlaceCount.optional(),
  placements: ReportClassPlacements,
})

export const ReportDivisionResults = z.object({
  divisionId: z.string().min(1),
  divisionCode: z.string().nullable(),
  divisionName: z.string(),
  divisionOrder: z.number().int().nullable(),
  classes: z.array(ReportClassResults),
})

export const ReportResultsResponse = z.object({
  showId: z.guid(),
  generatedAt: z.string().datetime(),
  divisions: z.array(ReportDivisionResults),
})

export const ReportAwardWinner = z.object({
  awardId: z.guid().optional(),
  exhibitId: EntryCode.optional().nullable(),
  entrantId: z.guid().nullable(),
  entrantName: z.string().nullable(),
  exhibitorId: z.guid().nullable(),
  exhibitorName: z.string().nullable(),
  entryTitle: z.string().nullable(),
  classId: z.guid().nullable(),
  classCode: z.string().nullable(),
  className: z.string().nullable(),
  divisionId: z.string().min(1).nullable(),
  divisionName: z.string().nullable(),
})

export const ShowStatsWinner = ReportAwardWinner.extend({
  entryId: z.guid().nullable(),
  divisionCode: z.string().nullable(),
  points: z.number().int().nonnegative().nullable(),
})

export const ShowStatsResponse = z.object({
  showId: z.guid(),
  checkedInExhibitsCount: z.number().int().nonnegative().optional(),
  checkedInEntrantsCount: z.number().int().nonnegative().optional(),
  entriesCount: z.number().int().nonnegative(),
  entrantsCount: z.number().int().nonnegative(),
  averageEntriesPerEntrant: z.number().finite().nonnegative().nullable(),
  entriesPerEntrantDistribution: z.array(
    z.object({
      entriesCount: z.number().int().positive(),
      entrantsCount: z.number().int().positive(),
    })
  ),
  enteredClassIds: z.array(z.guid()),
  clubsCount: z.number().int().nonnegative(),
  blackJarEntriesCount: z.number().int().nonnegative(),
  winners: z
    .object({
      grandChampion: ShowStatsWinner.nullable(),
      blackJarWinner: ShowStatsWinner.nullable(),
      bestInShow: z.array(ShowStatsWinner),
    })
    .optional(),
})

export const ShowEdition = z.object({
  id: z.guid(),
  name: z.string(),
  startDate: z.string().datetime(),
  editionYear: z.number().int(),
  status: ShowStatus,
  enableClubs: z.boolean(),
})

export const ComparisonMetric = z.object({
  current: z.number().finite().nullable(),
  previous: z.number().finite().nullable(),
  absoluteChange: z.number().finite().nullable(),
  percentChange: z.number().finite().nullable(),
})

export const ComparisonClass = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  catalogClassId: z.string().nullable(),
  categoryCode: z.string().nullable(),
  division: z.object({ id: z.guid(), code: z.string(), name: z.string() }),
})

/**
 * A row's class sits in one series identity (`aligned`, or `current_only` / `previous_only` when the
 * other edition offers no class in that identity), was explicitly left `unmatched`, or is still
 * `unresolved`. Null whenever no previous edition is being compared.
 */
export const ClassComparisonRow = ComparisonMetric.extend({
  key: z.string(),
  matchStatus: z
    .enum(['aligned', 'current_only', 'previous_only', 'unmatched', 'unresolved'])
    .nullable(),
  identityId: z.guid().nullable(),
  currentClass: ComparisonClass.nullable(),
  previousClass: ComparisonClass.nullable(),
})

export const ClubComparisonRow = ComparisonMetric.extend({
  key: z.string(),
  identityKind: z.enum(['club', 'manual', 'no_club']),
  currentClubId: z.string().nullable(),
  previousClubId: z.string().nullable(),
  currentLabel: z.string().nullable(),
  previousLabel: z.string().nullable(),
})

export const ShowStatsComparisonResponse = z.object({
  generatedAt: z.string().datetime(),
  currentShow: ShowEdition,
  previousShow: ShowEdition.nullable(),
  // The previous edition is the show's immediate predecessor in its series. `unavailable` means that
  // predecessor exists but the caller cannot read it; nothing about it is returned.
  comparisonState: z.enum(['available', 'no_series', 'first_edition', 'unavailable']),
  alignment: z.object({
    current: SeriesAnnualProjection.shape.coverage.nullable(),
    previous: SeriesAnnualProjection.shape.coverage.nullable(),
  }),
  summary: z.object({
    entriesCount: ComparisonMetric,
    entrantsCount: ComparisonMetric,
    averageEntriesPerEntrant: ComparisonMetric,
  }),
  classes: z.array(ClassComparisonRow),
  clubs: z.object({
    currentAvailable: z.boolean(),
    previousAvailable: z.boolean(),
    rows: z.array(ClubComparisonRow),
  }),
})

export const ReportBestInDivisionWinner = z.object({
  divisionId: z.string().min(1),
  divisionName: z.string(),
  divisionCode: z.string().nullable(),
  winner: ReportAwardWinner.nullable(),
})

export const HeroAwardCandidate = z.object({
  exhibitId: z.guid(),
  entryNumber: z.string(),
  categoryId: z.string(),
  entryId: z.guid().optional(),
  entrantName: z.string().optional(),
})
export const HeroAwardState = z.object({
  showName: z.string(),
  enabled: z.boolean(),
  ready: z.boolean(),
  unfinishedClasses: z.number().int(),
  placement: z.string().nullable(),
  candidates: z.array(HeroAwardCandidate),
  winner: HeroAwardCandidate.nullable(),
  reselectionRequired: z.boolean(),
  generatedAt: z.string(),
})
export const SetHeroAwardRequest = z.object({ exhibitId: z.guid() })
export type HeroAwardStateOutput = z.infer<typeof HeroAwardState>

export const ReportAwardsResponse = z.object({
  heroAward: HeroAwardState.optional(),
  showId: z.guid(),
  enableClubs: z.boolean(),
  generatedAt: z.string().datetime(),
  /** Represented clubs by points, highest first. */
  clubStandings: z.array(ReportClubStanding),
  clubSpiritAward: ReportClubAward,
  clubChampionAward: ReportClubAward,
  bestInShow: ReportAwardWinner.nullable(),
  reserveGrands: z.array(ReportAwardWinner).optional(),
  sweepstakes: ReportAwardWinner.nullable(),
  bestInDivision: z.array(ReportBestInDivisionWinner).optional(),
  bestInDivisionEnabled: z.boolean().optional(),
})

export const ShowSpecialAwardsResponse = z.object({
  heroAward: HeroAwardState.optional(),
  showId: z.guid(),
  bestInShow: ReportAwardWinner.nullable(),
  reserveGrands: z.array(ReportAwardWinner).optional(),
  grandChampion: ReportAwardWinner.nullable(),
  reserveGrand: ReportAwardWinner.nullable(),
  reserveGrandEnabled: z.boolean(),
  bestInDivisionEnabled: z.boolean(),
  bestInDivision: z.array(ReportBestInDivisionWinner),
})

export const SetBestInShowRequest = z.object({
  exhibitCode: EntryCode,
})

export const SetBestInDivisionRequest = z.object({
  exhibitCode: EntryCode,
})

export const SetChampionAwardsRequest = z
  .object({
    championEntrantId: z.guid().nullable().optional(),
    reserveEntrantId: z.guid().nullable().optional(),
    reserveEntrantIds: z.array(z.guid()).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.championEntrantId === undefined &&
      data.reserveEntrantId === undefined &&
      data.reserveEntrantIds === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Provide championEntrantId or reserveEntrantIds',
        path: ['championEntrantId'],
      })
    }
    const reserves =
      data.reserveEntrantIds ?? (data.reserveEntrantId ? [data.reserveEntrantId] : [])
    if (
      data.reserveEntrantIds !== undefined &&
      data.reserveEntrantId !== undefined &&
      (data.reserveEntrantId === null
        ? reserves.length !== 0
        : reserves.length !== 1 || reserves[0] !== data.reserveEntrantId)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Reserve selections conflict',
        path: ['reserveEntrantIds'],
      })
    }
    if (data.championEntrantId && reserves.includes(data.championEntrantId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Champion and Reserve must reference different entrants',
        path: ['reserveEntrantIds'],
      })
    }
  })

export const ReportMemberAwardPlacement = z.object({
  placement: z.string(),
  points: z.number(),
  classId: z.guid(),
  classCode: z.string().nullable(),
  className: z.string(),
  divisionId: z.string().min(1).nullable(),
  divisionName: z.string().nullable(),
  exhibitId: z.string(),
  entryTitle: z.string().nullable(),
})

export const ReportMemberAwardCard = z.object({
  exhibitorId: z.guid(),
  exhibitorName: z.string(),
  memberName: z.string(),
  memberId: z.string().nullable(),
  membershipExpiresAt: z.string().datetime().nullable(),
  staffRole: z.string().nullable(),
  placement: z.string(),
  points: z.number(),
  classId: z.guid(),
  classCode: z.string().nullable(),
  className: z.string(),
  divisionId: z.string().min(1).nullable(),
  divisionName: z.string().nullable(),
  exhibitId: z.string(),
  entryTitle: z.string().nullable(),
})

export const ReportMemberAwardRecipient = z.object({
  exhibitorId: z.guid(),
  exhibitorName: z.string(),
  memberName: z.string(),
  memberId: z.string().nullable(),
  membershipExpiresAt: z.string().datetime().nullable(),
  staffRole: z.string().nullable(),
  email: z.string().email().nullable(),
  totalPoints: z.number(),
  placements: z.array(ReportMemberAwardPlacement),
})

export const ReportAwardCardsResponse = z.object({
  showId: z.guid(),
  generatedAt: z.string().datetime(),
  showName: z.string(),
  showLocation: z.string().nullable(),
  showStartDate: z.string().datetime().nullable(),
  showEndDate: z.string().datetime().nullable(),
  summary: ReportSummaryResponse,
  cards: z.array(ReportMemberAwardCard),
  recipients: z.array(ReportMemberAwardRecipient),
})

export const ReportAwardsEmailPreviewResponse = z.object({
  showId: z.guid(),
  generatedAt: z.string().datetime(),
  exhibitorId: z.guid(),
  memberName: z.string(),
  subject: z.string(),
  html: z.string(),
  text: z.string(),
})

export const SendReportAwardsEmailRequest = z.object({
  exhibitorIds: z.array(z.guid()).optional(),
})

export const SendReportAwardsEmailResponse = z.object({
  showId: z.guid(),
  generatedAt: z.string().datetime(),
  requested: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  results: z.array(
    z.object({
      exhibitorId: z.guid(),
      email: z.string().email().nullable(),
      status: z.enum(['sent', 'skipped']),
      reason: z.string().nullable().optional(),
    })
  ),
})

export const AllocateIdentifiersRequest = z.object({
  count: z.number().int().min(1).max(500).optional(),
  exhibitorId: z.guid().optional(),
})

export const AllocateIdentifiersResponse = z.object({
  identifiers: z.array(IdentifierSummary),
})

export const IdentifierIntakeContextRequest = z.object({
  exhibitorId: z.guid(),
  codes: z.array(IntakeEntryCode).max(500),
})

export const IdentifierIntakeContextResponse = z.object({
  isShowStaff: z.boolean().optional(),
  candidates: z.array(
    z.object({
      value: IntakeEntryCode,
      orderIndex: z.number().int().nonnegative().nullable(),
      status: z.enum(['claimable', 'unavailable', 'unknown']),
    })
  ),
  assignedOrderIndexes: z.array(z.number().int().nonnegative()),
  complete: z.boolean(),
})

export type IdentifierIntakeContextInput = z.infer<typeof IdentifierIntakeContextRequest>
export type IdentifierIntakeContextOutput = z.infer<typeof IdentifierIntakeContextResponse>

export const IdentifierAvailabilityResponse = z.object({
  available: z.number().int().nonnegative(),
  assigned: z.number().int().nonnegative(),
  reserved: z.number().int().nonnegative(),
})

export const PersonOrganizationLink = z.object({
  organizationId: z.guid(),
  role: z.string().optional(),
  since: z.string().datetime().optional(),
  until: z.string().datetime().optional(),
  organizationName: z.string().optional(),
})

export const CredentialNumber = z.string().trim().min(1).max(16)

export const Person = z.object({
  credentialNumber: CredentialNumber.nullable().optional(),
  id: z.guid().optional(),
  name: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  ahstcMember: z.boolean().default(false),
  ahstcJudge: z.boolean().default(false),
  memberId: z.string().nullable().optional(),
  membershipExpiresAt: z.string().datetime().nullable().optional(),
  judgeCertificationLevel: z.string().nullable().optional(),
  judgeStatus: z.string().nullable().optional(),
  judgeTrainingDate: z.string().datetime().nullable().optional(),
  judgeDirectoryPhotoUrl: z.string().url().nullable().optional(),
  notes: z.string().nullable().optional(),
  displayCity: z.string().nullable().optional(),
  displayState: z.string().nullable().optional(),
  mailingAddress: z.string().nullable().optional(),
  mailingCity: z.string().nullable().optional(),
  mailingState: z.string().nullable().optional(),
  mailingZipCode: z.string().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  showsEntered: z.number().int().nonnegative().optional(),
  organizations: z.array(PersonOrganizationLink).optional(),
})

const PersonMutableFields = Person.pick({
  name: true,
  firstName: true,
  lastName: true,
  email: true,
  phone: true,
  notes: true,
  displayCity: true,
  displayState: true,
  mailingAddress: true,
  mailingCity: true,
  mailingState: true,
  mailingZipCode: true,
})

const PersonMembershipFields = z.object({
  ahstcMember: z.boolean().optional(),
  ahstcJudge: z.boolean().optional(),
  memberId: z.union([z.string(), z.null()]).optional(),
  membershipExpiresAt: z.union([z.string().datetime(), z.null()]).optional(),
  judgeCertificationLevel: z.union([z.string(), z.null()]).optional(),
  judgeStatus: z.union([z.string(), z.null()]).optional(),
  judgeTrainingDate: z.union([z.string().datetime(), z.null()]).optional(),
  judgeDirectoryPhotoUrl: z.union([z.string().url(), z.null()]).optional(),
})

export const CreatePersonRequest = PersonMutableFields.extend({
  ahstcMember: z.boolean().optional(),
  ahstcJudge: z.boolean().optional(),
  memberId: z.string().optional(),
  membershipExpiresAt: z.string().datetime().optional(),
  judgeCertificationLevel: z.string().optional(),
  judgeStatus: z.string().optional(),
  judgeTrainingDate: z.string().datetime().optional(),
  judgeDirectoryPhotoUrl: z.string().url().optional(),
})

export const UpdatePersonRequest = CreatePersonRequest.partial().refine(
  data => Object.values(data).some(value => value !== undefined),
  {
    message: 'At least one field must be provided',
  }
)

export const UpdatePersonMembershipRequest = PersonMembershipFields.refine(
  data => Object.values(data).some(value => value !== undefined),
  {
    message: 'At least one membership field must be provided',
  }
)

export const LinkPersonOrganizationRequest = z.object({
  organizationId: z.guid(),
  role: z.string().optional(),
  since: z.string().datetime().optional(),
  until: z.string().datetime().optional(),
})

export const MergePeopleRequest = z.object({
  sourcePersonId: z.guid(),
  strategy: z.enum(['preserve', 'overwrite']).optional(),
  // Review tokens from each person's participation history. When sent, the merge is refused
  // if either history has changed since it was reviewed.
  review: z.object({ target: z.string().min(1), source: z.string().min(1) }).optional(),
  // From the merge preview. Required when the merge updates a read-only show. When sent, the
  // merge is refused if what it would change, or either person's details that a merge combines,
  // are no longer what was previewed.
  planToken: z.string().min(1).optional(),
})

export const MergePeoplePreviewRequest = z.object({
  sourcePersonId: z.guid(),
  strategy: z.enum(['preserve', 'overwrite']).optional(),
})

export const MergePeopleResponse = z.object({
  person: Person,
  mergedFromPersonId: z.guid(),
  mergedExhibitors: z.number().int(),
  mergedAssignments: z.number().int(),
  mergedOrganizations: z.number().int(),
  strategy: z.enum(['preserve', 'overwrite']),
})

export const PaginationMetadata = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().positive(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
})

export const ListPeopleResponse = z.object({
  people: z.array(Person),
  pagination: PaginationMetadata,
})

export const ShowRole = z.enum([
  'organizer',
  'presiding_judge',
  'lead_secretary',
  'secretary',
  'steward',
  'judge',
  'viewer',
])

export const ShowRoleAssignment = z.object({
  id: z.guid().optional(),
  showId: z.guid(),
  role: ShowRole,
  personId: z.guid().nullable().optional(),
  name: z.string().nullable().optional(),
  contact: z.string().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  person: Person.pick({
    id: true,
    name: true,
    email: true,
    phone: true,
    ahstcMember: true,
    credentialNumber: true,
  })
    .nullable()
    .optional(),
  /** Staff lists only: whether the person has an account; null for a custom contact. */
  signInStatus: z.enum(['active', 'invited', 'disabled', 'none']).nullable().optional(),
})

export const CreateShowRoleAssignmentRequest = z
  .object({
    role: ShowRole,
    personId: z.guid().optional(),
    matchProof: z.string().optional(),
    name: z.string().optional(),
    contact: z.string().optional(),
  })
  .refine(data => data.personId || data.name, {
    message: 'Provide either personId or name when assigning staff',
    path: ['personId'],
  })

export const UpdateShowRoleAssignmentRequest = z.object({
  personId: z.guid().nullable().optional(),
  matchProof: z.string().optional(),
  name: z.string().nullable().optional(),
  contact: z.string().nullable().optional(),
})

export const ShowRoleAssignmentsResponse = z.object({
  assignments: z.array(ShowRoleAssignment),
})

export const ShowJudge = z.object({
  id: z.guid(),
  name: z.string().nullable(),
})

export const ShowJudgesResponse = z.object({
  judges: z.array(ShowJudge),
})

export const UpdateClassJudgesRequest = z.strictObject({
  judgeAssignmentIds: z
    .array(z.guid())
    .min(1)
    .refine(ids => new Set(ids).size === ids.length, 'Judge assignments must be unique'),
})

export const CatalogProvenance = z.object({
  origin: z.enum(['legacy', 'app', 'wpcivi', 'authored']),
  sourceInstanceId: z.string().nullable(),
  sourceKey: z.string().nullable(),
  importedDigest: z.string().nullable(),
  publicationVersion: z.string().nullable(),
})

export const CatalogSubtype = CatalogProvenance.extend({
  id: z.string().min(1),
  catalogClassId: z.string().min(1),
  name: z.string(),
  description: z.string().nullable(),
  retired: z.boolean(),
  revision: z.number().int().nonnegative(),
})
export const CreateCatalogSubtypeRequest = z.strictObject({
  expectedRevision: z.number().int().nonnegative(),
  name: z.string().trim().min(1),
  description: z.string().nullable().optional(),
})
export const UpdateCatalogSubtypeRequest = z
  .strictObject({
    expectedRevision: z.number().int().nonnegative(),
    expectedModelRevision: z.number().int().nonnegative(),
    name: z.string().trim().min(1).optional(),
    description: z.string().nullable().optional(),
    retired: z.boolean().optional(),
  })
  .refine(
    data => data.name !== undefined || data.description !== undefined || data.retired !== undefined,
    {
      message: 'At least one field must be provided',
    }
  )
export type CreateCatalogSubtypeInput = z.infer<typeof CreateCatalogSubtypeRequest>
export type UpdateCatalogSubtypeInput = z.infer<typeof UpdateCatalogSubtypeRequest>

export const CatalogClass = CatalogProvenance.extend({
  id: z.string().min(1),
  categoryCode: z.string(),
  categoryName: z.string().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  aliases: FlexibleStringArray.optional(),
  flags: CatalogFlags.optional(),
  rules: CatalogRules,
  retired: z.boolean(),
  revision: z.number().int().nonnegative(),
  subtypes: z.array(CatalogSubtype),
  requiresSubtype: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const CatalogClassSummary = z.object({
  id: z.string(),
  categoryCode: z.string(),
  categoryName: z.string(),
  name: z.string(),
  flags: CatalogFlags.optional(),
  rules: CatalogRules.optional(),
})

export const ClassModelDefaults = z.object({
  kind: z.enum(['adopted', 'legacy_baseline']),
  capturedAt: z.string().datetime(),
  adoptedAt: z.string().datetime().nullable(),
  adoptedBy: z.string().nullable(),
  model: CatalogProvenance.extend({ id: z.string(), revision: z.number().int() }).nullable(),
  subtype: CatalogSubtype.nullable(),
  defaults: CatalogClassSummary.extend({
    description: z.string().nullable().optional(),
  }).nullable(),
})
export const ModelDefaultsPreview = z.object({
  reviewToken: z.string(),
  current: ClassModelDefaults.nullable(),
  proposed: ClassModelDefaults,
  preserved: z.record(z.string(), z.unknown()),
})
export const ApplyModelDefaultsRequest = z.strictObject({ reviewToken: z.string().min(1) })
export type TClassModelDefaults = z.infer<typeof ClassModelDefaults>
export type TModelDefaultsPreview = z.infer<typeof ModelDefaultsPreview>

export const CreateCatalogClassRequest = z.strictObject({
  categoryCode: z.string().trim().min(1),
  name: z.string().trim().min(1),
  description: z.string().optional(),
  aliases: FlexibleStringArray.optional(),
  flags: CatalogFlags.optional(),
  rules: CatalogRules,
})

export const UpdateCatalogClassRequest = z
  .strictObject({
    expectedRevision: z.number().int().nonnegative(),
    retired: z.boolean().optional(),
    categoryCode: z.string().trim().min(1).optional(),
    name: z.string().trim().min(1).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    aliases: z.union([FlexibleStringArray, z.null()]).optional(),
    flags: z.union([CatalogFlags, z.null()]).optional(),
    rules: z.union([CatalogRules, z.null()]).optional(),
  })
  .refine(
    data =>
      Object.entries(data).some(
        ([key, value]) => key !== 'expectedRevision' && value !== undefined
      ),
    {
      message: 'At least one field must be provided',
    }
  )

export const CatalogClassResponse = z.object({
  catalogClass: CatalogClass,
})

export const ListOrganizationsResponse = z.object({
  organizations: z.array(OrganizationWithShows),
})

export const UpdateOrganizationRequest = CreateOrganizationRequest.partial().refine(
  data => Object.values(data).some(value => value !== undefined),
  {
    message: 'At least one field must be provided',
  }
)

export const UpdateShowRequest = z
  .object({
    seriesId: z.guid().nullable().optional(),
    expectedMembershipRevision: z.number().int().nonnegative().optional(),
    name: z.string().min(1).optional(),
    orgId: z.guid().optional(),
    slug: ShowSlug.optional(),
    startDate: ShowDateString.optional(),
    endDate: ShowDateString.optional(),
    location: z.string().optional(),
    hostState: HostState.nullish(),
    basicDetailsPublished: z.boolean().optional(),
    status: ShowStatus.optional(),
    finalizeEntrantsForJudging: z.literal(true).optional(),
    rulesVersion: z.string().optional(),
    exhibitPermMod: z.number().int().min(2).optional(),
    awardsPolicy: AwardsPolicy.nullish().optional(),
    applyTiebreakers: z.boolean().optional(),
    enableClubs: z.boolean().optional(),
    labelOnlyMode: z.boolean().optional(),
    labelPrintingDisabled: z.boolean().optional(),
    staffMayExhibit: z.boolean().optional(),
    allowStaffEntries: z.boolean().optional(),
    reserveGrandEnabled: z.boolean().optional(),
    defaultPlaceCount: ShowPlaceCount.optional(),
  })
  .refine(data => data.seriesId === undefined || data.expectedMembershipRevision !== undefined, {
    message: 'Expected membership revision is required when selecting or clearing a series',
    path: ['expectedMembershipRevision'],
  })
  .refine(data => Object.values(data).some(value => value !== undefined), {
    message: 'At least one field must be provided',
  })
  .refine(
    data =>
      !data.finalizeEntrantsForJudging ||
      (data.status === 'judging' &&
        Object.keys(data).every(key => key === 'status' || key === 'finalizeEntrantsForJudging')),
    {
      message:
        'Bulk finalization requires status judging and cannot be combined with other show changes',
    }
  )

export const AddCatalogClassToShowRequest = z.object({
  catalogClassId: z.string().min(1),
  catalogSubtypeId: z.string().min(1).optional(),
  divisionId: z.string().min(1).optional(),
  divisionCode: z.string().optional(),
  divisionName: z.string().optional(),
  divisionOrder: z.number().int().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  ruleOverrides: z.union([z.string(), CatalogRules]).optional(),
  parameters: z.union([z.string(), CatalogRules]).optional(),
})

export const ListShowsResponse = z.object({
  shows: z.array(Show),
})

export const Division = z.object({
  id: z.string().min(1).optional(),
  showId: z.guid(),
  code: z.string(),
  name: z.string(),
  order: z.number().int(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const Class = z.object({
  id: z.guid().optional(),
  divisionId: z.string().min(1),
  code: z.string(),
  order: z.number().int(),
  name: z.string(),
  description: z.string().nullable().optional(),
  rules: z.string().nullable().optional(),
  maxEntriesPerExhibitor: z.number().int().positive().nullable().optional(),
  judgingStartedAt: z.string().datetime().nullable().optional(),
  catalogClassId: z.string().nullable().optional(),
  catalogSubtypeId: z.string().nullable().optional(),
  modelDefaults: ClassModelDefaults.nullable().optional(),
  displayName: z.string().nullable().optional(),
  ruleOverrides: z
    .union([z.string(), z.record(z.string(), z.unknown())])
    .nullable()
    .optional(),
  parameters: z
    .union([z.string(), z.record(z.string(), z.unknown())])
    .nullable()
    .optional(),
  noAwards: z.boolean().optional(),
  /** The class's own place count; null awards the show's default. */
  placeCount: ClassPlaceCount.nullable().optional(),
  grandChampionEligible: z.boolean().optional(),
  hasPlacements: z.boolean().optional(),
  resultsFinalizedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  catalogClass: CatalogClassSummary.optional(),
})

export const UpdateShowClassRequest = z
  .object({
    name: z.string().optional(),
    displayName: z.union([z.string(), z.null()]).optional(),
    ruleOverrides: z.union([z.string(), CatalogRules, z.null()]).optional(),
    parameters: z.union([z.string(), CatalogRules, z.null()]).optional(),
    grandChampionEligible: z.boolean().optional(),
    placeCount: ClassPlaceCount.nullable().optional(),
  })
  .refine(data => Object.values(data).some(value => value !== undefined), {
    message: 'At least one field must be provided',
  })

export const UpdateClassFinalizationRequest = z
  .object({
    noAwards: z.boolean().optional(),
    finalized: z.boolean().optional(),
  })
  .refine(data => Object.values(data).some(value => value !== undefined), {
    message: 'At least one field must be provided',
  })

export const UpdateClassFinalizationResponse = z.object({
  class: Class,
})

export const UpdateShowClassResponse = z.object({
  class: Class,
})

export const ListCatalogCategoriesResponse = z.object({
  categories: z.array(CatalogCategory),
})

export const ListCatalogClassesResponse = z.object({
  classes: z.array(CatalogClass),
})

export const AddCatalogClassToShowResponse = z.object({
  division: Division,
  class: Class,
  catalogClass: CatalogClassSummary.optional(),
})

export const CatalogClassUsageInstance = z.object({
  show: z.object({
    id: z.guid(),
    name: z.string(),
    status: z.string(),
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
  }),
  division: z.object({
    id: z.guid(),
    code: z.string(),
    name: z.string(),
    order: z.number().int(),
  }),
  class: z.object({
    id: z.guid(),
    code: z.string(),
    name: z.string(),
    order: z.number().int(),
    noAwards: z.boolean(),
    resultsFinalizedAt: z.union([z.string().datetime(), z.null()]),
  }),
})

export const CatalogClassUsageResponse = z.object({
  catalogClass: CatalogClassSummary,
  totals: z.object({
    shows: z.number().int(),
    classes: z.number().int(),
  }),
  instances: z.array(CatalogClassUsageInstance),
})

export const ReindexClassCodesRequest = z.object({
  divisionId: z.string().min(1).optional(),
})

export const ReindexClassCodesResponse = z.object({
  divisions: z.array(
    z.object({
      divisionId: z.string().min(1),
      updated: z.array(
        z.object({
          classId: z.guid(),
          from: z.string(),
          to: z.string(),
        })
      ),
    })
  ),
})

export const EntrantPacketRequest = z.object({
  showId: z.guid(),
  exhibitorId: z.guid(),
})

export const CreateDivisionsRequest = z.object({
  divisions: z
    .array(
      z.object({
        code: z.string(),
        name: z.string(),
        order: z.number().int(),
        classes: z
          .array(
            z.object({
              code: z.string(),
              order: z.number().int().optional(),
              name: z.string(),
              description: z.string().optional(),
              rules: z.string().optional(),
              maxEntriesPerExhibitor: z.number().int().positive().optional(),
              catalogClassId: z.string().min(1),
              catalogSubtypeId: z.string().min(1).optional(),
              displayName: z.string().optional(),
              ruleOverrides: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
              parameters: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
              grandChampionEligible: z.boolean().optional(),
            })
          )
          .min(1),
      })
    )
    .min(1),
})

export const Exhibitor = z.object({
  id: z.guid().optional(),
  orgId: z.guid().nullable().optional(),
  personId: z.guid().nullable().optional(),
  name: z.string(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  affiliation: z.string().optional(),
  showClubId: ClubId.nullable().optional(),
  showClubName: z.string().nullable().optional(),
  lastKnownClubId: ClubId.nullable().optional(),
  lastKnownClubName: z.string().nullable().optional(),
  grandChampionEligible: z.boolean().optional(),
  heroAwardEligible: z.boolean().optional(),
  finalizedAt: z.string().datetime().nullable().optional(),
  finalizedBy: z.string().nullable().optional(),
  finalizedByRole: z.string().nullable().optional(),
  entrantCode: z
    .string()
    .max(6)
    .regex(/^[A-Za-z0-9-]+$/, { message: 'Entrant code must be alphanumeric' })
    .optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const CreateExhibitorRequest = z.object({
  orgId: z.guid().nullable().optional(),
  personId: z.guid().nullable().optional(),
  name: z.string().min(1),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  affiliation: z.string().optional(),
  entrantCode: z
    .string()
    .max(6)
    .regex(/^[A-Za-z0-9-]+$/, { message: 'Entrant code must be alphanumeric' })
    .optional(),
})

export const UpdateShowExhibitorRequest = z
  .object({
    grandChampionEligible: z.boolean().optional(),
    heroAwardEligible: z.boolean().optional(),
    clubId: ClubId.nullable().optional(),
    clubName: z
      .string()
      .min(1)
      .nullable()
      .optional()
      .describe(
        'Manual club name when no clubId is selected; required when show.enableClubs is true.'
      ),
  })
  .refine(
    data =>
      data.grandChampionEligible !== undefined ||
      data.heroAwardEligible !== undefined ||
      data.clubId !== undefined ||
      data.clubName !== undefined,
    {
      message: 'No updates provided.',
    }
  )

export const ShowEntrant = z.object({
  showId: z.guid(),
  exhibitorId: z.guid(),
  grandChampionEligible: z.boolean(),
  heroAwardEligible: z.boolean().optional(),
  clubId: ClubId.nullable().optional(),
  clubName: z.string().nullable().optional(),
  finalizedAt: z.string().datetime().nullable(),
  finalizedBy: z.string().nullable(),
  finalizedByRole: z.string().nullable(),
  updatedAt: z.string().datetime(),
})

export const UpdateShowExhibitorResponse = z.object({
  showEntrant: ShowEntrant,
})

export const UpdateShowEntrantFinalizationRequest = z.object({
  finalized: z.boolean(),
})

export const UpdateShowEntrantFinalizationResponse = z.object({
  showEntrant: ShowEntrant,
})

const PersonSummary = Person.pick({
  id: true,
  name: true,
  email: true,
  phone: true,
  ahstcMember: true,
  ahstcJudge: true,
  judgeCertificationLevel: true,
  judgeStatus: true,
})

export const EntryStatus = z.enum([
  'draft',
  'checked_in',
  'not_entered',
  'judged',
  'disqualified',
  'finalized',
])

/**
 * Statuses that take an entry out of competition: it holds no class, takes no placement or award
 * and is left out of standings. Show totals and fees still count a Disqualified entry.
 */
export const OUT_OF_COMPETITION_ENTRY_STATUSES = [
  'not_entered',
  'disqualified',
] as const satisfies ReadonlyArray<z.infer<typeof EntryStatus>>

export function entryInCompetition(status: string | null | undefined): boolean {
  return !(OUT_OF_COMPETITION_ENTRY_STATUSES as ReadonlyArray<string>).includes(status ?? '')
}

export const EntrySummary = z.object({
  id: z.guid(),
  entryCode: EntryCode,
  exhibitId: z.guid().nullable().optional(),
  title: z.string().nullable().optional(),
  status: EntryStatus,
  overrideDuplicateReason: z.string().min(1).nullable().optional(),
  overrideDuplicateBy: z.string().nullable().optional(),
  overrideDuplicateAt: z.string().datetime().nullable().optional(),
  class: z
    .object({
      id: z.guid(),
      code: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
})

export const ExhibitorWithEntries = Exhibitor.extend({
  entries: z.array(EntrySummary).optional(),
  person: PersonSummary.nullable().optional(),
})

// A person's history across the shows the caller may see. One person can hold several
// exhibitor records in a show; a show appears once and its entries are counted together.
export const PersonParticipationShow = z.object({
  show: z.object({
    id: z.guid(),
    slug: ShowSlug,
    name: z.string(),
    startDate: ShowDateString,
    endDate: ShowDateString,
    status: ShowStatus,
    organization: z.object({ id: z.guid(), name: z.string() }),
  }),
  roles: z.array(ShowRole),
  entrantRegistered: z.boolean(),
  entrantRecords: z.number().int().nonnegative(),
  entryCount: z.number().int().nonnegative(),
})

export const PersonParticipationResponse = z.object({
  personId: z.guid(),
  reviewToken: z.string(),
  totals: z.object({
    shows: z.number().int().nonnegative(),
    showsEntered: z.number().int().nonnegative(),
    entries: z.number().int().nonnegative(),
  }),
  shows: z.array(PersonParticipationShow),
  pagination: PaginationMetadata,
})

export const PersonParticipationEntry = z.object({
  id: z.guid(),
  entryCode: EntryCode,
  title: z.string().nullable(),
  status: EntryStatus,
  exhibitorId: z.guid(),
  class: z.object({ id: z.guid(), code: z.string(), name: z.string() }),
})

export const PersonParticipationEntriesResponse = z.object({
  entries: z.array(PersonParticipationEntry),
  pagination: PaginationMetadata,
})

const MergePreviewShow = z.object({
  id: z.guid(),
  slug: ShowSlug,
  name: z.string(),
  startDate: ShowDateString,
  endDate: ShowDateString,
})

// Why a show prevents a merge: it is mid-handover, run elsewhere, or about to receive a handover.
export const MergeBlockedShow = MergePreviewShow.extend({
  reason: z.enum(['outgoing', 'inactive', 'handover_prepared', 'not_owned']),
})

const MergedPersonValue = z.union([z.string(), z.boolean()]).nullable()

// What a merge would do, computed by the server for one pair and strategy. Shows are named only
// when the caller manages people in them; the rest are counted.
export const MergePeoplePreviewResponse = z.object({
  planToken: z.string(),
  strategy: z.enum(['preserve', 'overwrite']),
  mergeable: z.boolean(),
  refusal: z.enum(['linked_account', 'credential_direction']).nullable(),
  keptChanges: z.array(
    z.object({
      field: z.enum([
        'email',
        'phone',
        'notes',
        'ahstcMember',
        'ahstcJudge',
        'memberId',
        'membershipExpiresAt',
        'judgeCertificationLevel',
        'judgeStatus',
        'judgeTrainingDate',
        'judgeDirectoryPhotoUrl',
      ]),
      from: MergedPersonValue,
      to: MergedPersonValue,
    })
  ),
  contactCorrectionFields: z
    .object({
      retained: z.array(z.string()),
      adopted: z.array(z.string()),
      released: z.array(z.string()),
    })
    .optional(),
  // Read-only shows held on this installation whose records the merge will update.
  readOnlyShows: z.array(MergePreviewShow),
  blockedShows: z.array(MergeBlockedShow),
  outsideScope: z.object({
    readOnly: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    classConflicts: z.number().int().nonnegative(),
  }),
  // After the merge the kept person is part of a show that is being handed over, so their details
  // cannot be changed until that handover is complete or cancelled.
  keptDetailsLocked: z.boolean(),
  // Classes in which each person has an active entry. The merge still goes ahead; the kept person
  // then has two active entries there until staff mark one Not Entered.
  classConflicts: z.array(
    z.object({
      show: MergePreviewShow,
      divisionName: z.string(),
      className: z.string(),
      entryCodes: z.array(z.string()),
    })
  ),
})

const EntryDetailClass = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  divisionId: z.guid().nullable().optional(),
  divisionName: z.string().nullable().optional(),
})

const EntryDetailShow = z.object({
  id: z.guid(),
  status: ShowStatus,
  labelOnlyMode: z.boolean(),
  labelPrintingDisabled: z.boolean(),
})

export const GetShowExhibitorByPersonResponse = z.object({
  exhibitor: ExhibitorWithEntries,
})

export const ListExhibitorsResponse = z.object({
  exhibitors: z.array(ExhibitorWithEntries),
})

export const Entry = z.object({
  id: z.guid().optional(),
  showId: z.guid(),
  classId: z.guid(),
  exhibitorId: z.guid(),
  clubId: ClubId.nullable().optional(),
  entryCode: EntryCode,
  title: z.string().optional(),
  notes: z.string().optional(),
  overrideDuplicateReason: z.string().min(1).nullable().optional(),
  overrideDuplicateBy: z.string().min(1).nullable().optional(),
  overrideDuplicateAt: z.string().datetime().nullable().optional(),
  fees: z.number().nonnegative().default(0),
  clubName: z.string().min(1).nullable().optional(),
  status: EntryStatus.default('draft'),
  notEnteredAt: z.string().datetime().nullable().optional(),
  notEnteredBy: z.string().nullable().optional(),
  notEnteredByRole: z.string().nullable().optional(),
  notEnteredReason: z.string().nullable().optional(),
  disqualifiedAt: z.string().datetime().nullable().optional(),
  disqualifiedBy: z.string().nullable().optional(),
  disqualifiedReason: z.string().nullable().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const EntrySubmissionConflict = z.object({
  status: z.enum([
    'submission_conflict',
    'allocation_order_conflict',
    'exhibit_id_conflict',
    'identifier_exhausted',
    'conflict',
  ]),
  message: z.string(),
})

export const CreateEntryRequest = Entry.pick({
  showId: true,
  classId: true,
  exhibitorId: true,
  title: true,
  notes: true,
  fees: true,
}).extend({
  entryCode: IntakeEntryCode.optional(),
  submissionId: z.guid().optional(),
  identifier: IntakeEntryCode.optional(),
  clubId: Entry.shape.clubId.describe(
    'Identifier of an existing club; required when show.enableClubs is true.'
  ),
  clubName: z
    .string()
    .min(1)
    .optional()
    .describe(
      'Manual club name when no clubId is selected; required when show.enableClubs is true.'
    ),
})

export const Exhibit = z.object({
  id: z.guid().optional(),
  entryId: z.guid(),
  showId: z.guid(),
  stagingLocation: z.string().optional(),
  labelState: z.enum(['queued', 'printed', 'error']).default('queued'),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

const EntryDetailExhibit = Exhibit.pick({
  id: true,
  labelState: true,
})

export const CheckInRequest = z.object({
  stagingLocation: z.string().optional(),
  identifier: IntakeEntryCode.optional(),
})

export const EntryDetail = Entry.extend({
  class: EntryDetailClass.nullable().optional(),
  exhibitor: ExhibitorWithEntries.optional(),
  show: EntryDetailShow,
  exhibit: EntryDetailExhibit.nullable().optional(),
})

export const GetEntryResponse = z.object({
  entry: EntryDetail,
})

export const UpdateEntryRequest = z.object({
  classId: z.guid(),
})

export const UpdateEntryResponse = z.object({
  entry: Entry,
})

export const MarkEntryNotEnteredRequest = z.object({
  reason: z.string().trim().max(500).optional(),
})

export const MarkEntryNotEnteredResponse = z.object({
  entry: Entry,
})

/**
 * Moves a checked-in exhibit to another class while the show is judging. When the entrant already
 * holds that class the server answers `class_conflict`; the repeat names the exhibit the judge
 * keeps and the conflicting entries the secretary was shown, and the others are disqualified.
 */
export const JudgingClassMoveRequest = z.object({
  showId: z.guid(),
  entryCode: EntryCode,
  classId: z.guid(),
  keepEntryId: z.guid().optional(),
  conflictingEntryIds: z.array(z.guid()).optional(),
})

const JudgingClassMoveClass = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
})

const JudgingClassMoveEntry = z.object({
  id: z.guid(),
  entryCode: EntryCode,
  status: EntryStatus,
})

export const JudgingClassMoveResponse = z.object({
  entry: JudgingClassMoveEntry,
  fromClass: JudgingClassMoveClass,
  class: JudgingClassMoveClass,
  disqualifiedEntries: z.array(JudgingClassMoveEntry),
  reopenedResults: z.boolean(),
})

export const JudgingClassMoveConflict = z.object({
  status: z.literal('class_conflict'),
  message: z.string(),
  entry: JudgingClassMoveEntry.extend({ class: JudgingClassMoveClass }),
  class: JudgingClassMoveClass,
  conflictingEntries: z.array(JudgingClassMoveEntry),
})

const PlacementEntries = z.object({
  '1st': z.array(EntryCode).default([]),
  '2nd': z.array(EntryCode).default([]),
  '3rd': z.array(EntryCode).default([]),
  '4th': z.array(EntryCode).default([]),
  '5th': z.array(EntryCode).default([]),
  '6th': z.array(EntryCode).default([]),
  '7th': z.array(EntryCode).default([]),
  '8th': z.array(EntryCode).default([]),
  '9th': z.array(EntryCode).default([]),
  '10th': z.array(EntryCode).default([]),
})

export const ClassPlacementsByEntryCodesRequest = z
  .object({
    showId: z.guid(),
    classId: z.guid(),
    placements: PlacementEntries,
    finalizeClass: z.boolean().optional(),
    // Clears a saved No awards choice in the same transaction as the placements.
    clearNoAwards: z.boolean().optional(),
  })
  .refine(data => CLASS_PLACEMENT_KEYS.some(place => data.placements[place].length > 0), {
    message: 'At least one placement is required',
    path: ['placements'],
  })

export const ClassSummary = Class.pick({
  id: true,
  code: true,
  name: true,
  divisionId: true,
}).extend({
  divisionName: z.string().optional(),
})

export const Score = z.object({
  id: z.guid().optional(),
  exhibitId: z.guid(),
  judgeId: z.guid(),
  criterion: z.string(),
  points: z.number().nonnegative(),
  comment: z.string().optional(),
  createdAt: z.string().datetime().optional(),
})

export const SubmitScoresRequest = z.object({
  scores: z
    .array(
      Score.pick({ exhibitId: true, judgeId: true, criterion: true, points: true, comment: true })
    )
    .min(1),
})

const AwardTypeEnum = z.enum([
  'class',
  'section',
  'best_of_show',
  'hero_award',
  'best_in_show',
  'best_in_division',
  'grand_champion',
  'reserve_grand',
  'special',
])

const awardSubjectFields = {
  exhibitId: z.guid().nullable().optional(),
  entrantId: z.guid().nullable().optional(),
} as const

function validateAwardSubject(
  data: { exhibitId?: string | null; entrantId?: string | null },
  ctx: z.RefinementCtx
) {
  const hasExhibit = typeof data.exhibitId === 'string'
  const hasEntrant = typeof data.entrantId === 'string'
  if (hasExhibit === hasEntrant) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Provide either exhibitId or entrantId for an award.',
      path: ['entrantId'],
    })
  }
}

export const Award = z
  .object({
    id: z.guid().optional(),
    showId: z.guid(),
    type: AwardTypeEnum,
    place: z.string(),
    notes: z.string().optional(),
    divisionId: z.string().min(1).optional().nullable(),
    createdAt: z.string().datetime().optional(),
    ...awardSubjectFields,
  })
  .superRefine(validateAwardSubject)

export const ClassPlacementsByEntryCodesResponse = z.object({
  awards: z.array(Award),
  classId: z.guid(),
  class: ClassSummary.extend({
    resultsFinalizedAt: z.string().datetime().nullable(),
    judgingStartedAt: z.string().datetime().nullable(),
    noAwards: z.boolean(),
  }).optional(),
})

const FinalizeAwardInput = z
  .object({
    type: AwardTypeEnum.exclude(['hero_award']),
    place: z.string(),
    notes: z.string().optional(),
    ...awardSubjectFields,
  })
  .superRefine(validateAwardSubject)

export const FinalizeAwardsRequest = z.object({
  showId: z.guid(),
  awards: z.array(FinalizeAwardInput),
})

export const AuditEvent = z.object({
  id: z.guid().optional(),
  actorId: z.string(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  before: z.string().optional(),
  after: z.string().optional(),
  createdAt: z.string().datetime().optional(),
})

const NullableString = z.string().nullable().optional()

export const WpciviSourceId = z.object({
  system: z.literal('wpcivi').default('wpcivi'),
  type: z.string().min(1),
  id: z.union([z.string().min(1), z.number().int().positive()]).transform(String),
})

export const WpciviOrganization = z.object({
  sourceId: WpciviSourceId,
  name: z.string().min(1),
  contact: NullableString,
  abbreviation: NullableString,
  website: NullableString,
})

export const WpciviClub = z.object({
  sourceId: WpciviSourceId,
  name: z.string().min(1),
  organizationSourceId: WpciviSourceId.nullable().optional(),
  parentOrganizationSourceId: WpciviSourceId.nullable().optional(),
  relationshipSourceId: WpciviSourceId.nullable().optional(),
})

export const WpciviPerson = z.object({
  sourceId: WpciviSourceId,
  name: z.string().min(1),
  firstName: NullableString,
  lastName: NullableString,
  email: NullableString,
  phone: NullableString,
  displayCity: NullableString,
  displayState: NullableString,
  mailingAddress: NullableString,
  mailingCity: NullableString,
  mailingState: NullableString,
  mailingZipCode: NullableString,
  credentialNumber: CredentialNumber.nullable().optional(),
  memberId: NullableString,
  membershipExpiresAt: z.string().datetime().nullable().optional(),
  ahstcMember: z.boolean().optional(),
  ahstcJudge: z.boolean().optional(),
  judgeCertificationLevel: NullableString,
  judgeStatus: NullableString,
  judgeTrainingDate: z.string().datetime().nullable().optional(),
  judgeDirectoryPhotoUrl: z.string().url().nullable().optional(),
  organizationSourceIds: z.array(WpciviSourceId).optional(),
})

export const WpciviMembership = z.object({
  sourceId: WpciviSourceId,
  personSourceId: WpciviSourceId,
  membershipType: z.string().optional(),
  status: z.string().optional(),
  startDate: NullableString,
  endDate: NullableString,
})

export const WpciviJudge = z.object({
  personSourceId: WpciviSourceId,
  certificationLevel: NullableString,
  status: NullableString,
  trainingDate: z.string().datetime().nullable().optional(),
  directoryPhotoUrl: z.string().url().nullable().optional(),
})

export const WpciviShow = z.object({
  sourceId: WpciviSourceId,
  wpPostSourceId: WpciviSourceId.nullable().optional(),
  hostOrganizationSourceId: WpciviSourceId.nullable().optional(),
  name: z.string().min(1),
  slug: NullableString,
  startDate: ShowDateString,
  endDate: ShowDateString,
  location: NullableString,
  hostState: HostState.nullish(),
  status: z.string().optional(),
  rulesPdfUrl: z.string().url().nullable().optional(),
  affidavitPdfUrl: z.string().url().nullable().optional(),
  ruleSnapshot: z
    .object({
      sourceId: WpciviSourceId,
      sourceChecksum: z.string(),
      rulesVersion: NullableString,
      title: NullableString,
      documentUrl: z.string().url().nullable().optional(),
      docxUrl: z.string().url().nullable().optional(),
      snapshot: z.record(z.string(), z.unknown()),
      metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    })
    .nullable()
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

export const WpciviShowCategory = z.object({
  showSourceId: WpciviSourceId,
  categoryName: z.string().min(1),
  categoryCode: NullableString,
  selected: z.boolean().default(true),
})

export const WpciviStaffAssignment = z.object({
  sourceId: WpciviSourceId,
  showSourceId: WpciviSourceId,
  personSourceId: WpciviSourceId.nullable().optional(),
  role: z.string().min(1),
  name: NullableString,
  contact: NullableString,
  resourceDemandSourceId: WpciviSourceId.nullable().optional(),
  resourceSourceId: WpciviSourceId.nullable().optional(),
  assignmentStatus: z.number().int().optional(),
  requiredCount: z.number().int().nonnegative().optional(),
  resourceTypeName: NullableString.optional(),
  resourceTypeLabel: NullableString.optional(),
})

export const WpciviParticipant = z.object({
  sourceId: WpciviSourceId,
  showSourceId: WpciviSourceId,
  personSourceId: WpciviSourceId.nullable().optional(),
  role: z.string().optional(),
  status: z.string().optional(),
  isExhibitor: z.boolean().optional(),
})

export const WpciviExportBundle = z.object({
  schemaVersion: z.literal('wpcivi-honeyshow.v1'),
  exportedAt: z.string().datetime(),
  organizations: z.array(WpciviOrganization).default([]),
  clubs: z.array(WpciviClub).default([]),
  people: z.array(WpciviPerson).default([]),
  memberships: z.array(WpciviMembership).default([]),
  judges: z.array(WpciviJudge).default([]),
  shows: z.array(WpciviShow).default([]),
  showCategories: z.array(WpciviShowCategory).default([]),
  staffAssignments: z.array(WpciviStaffAssignment).default([]),
  participants: z.array(WpciviParticipant).default([]),
  sourceIds: z.array(WpciviSourceId).default([]),
})

export const HoneyFeedbackBundle = z.object({
  schemaVersion: z.literal('honeyshow-feedback.v1'),
  generatedAt: z.string().datetime(),
  show: z.record(z.string(), z.unknown()),
  summary: z.record(z.string(), z.unknown()),
  staff: z.array(z.record(z.string(), z.unknown())).default([]),
  entrants: z.array(z.record(z.string(), z.unknown())).default([]),
  results: z.record(z.string(), z.unknown()),
  awards: z.record(z.string(), z.unknown()),
  documents: z.record(z.string(), z.unknown()),
  contactUpdateSuggestions: z.array(z.record(z.string(), z.unknown())).default([]),
  syncIssues: z.array(z.record(z.string(), z.unknown())).default([]),
})

export const Rubric = z.object({
  id: z.guid().optional(),
  name: z.string(),
  criteria: z.array(RubricCriterion).min(1),
})

export type TRubricCriterion = z.infer<typeof RubricCriterion>
export type TRubric = z.infer<typeof Rubric>
export type TOrganization = z.infer<typeof Organization>
export type TOrganizationWithShows = z.infer<typeof OrganizationWithShows>
export type TLeaderboardEntry = z.infer<typeof LeaderboardEntry>
export type TUpdateOrganizationInput = z.infer<typeof UpdateOrganizationRequest>
export type TShowStatus = z.infer<typeof ShowStatus>
export type TShow = z.infer<typeof Show>
export type TDivision = z.infer<typeof Division>
export type TClass = z.infer<typeof Class>
export type TExhibitor = z.infer<typeof Exhibitor>
export type TShowEntrant = z.infer<typeof ShowEntrant>
export type TClub = z.infer<typeof Club>
export type TEntrySummary = z.infer<typeof EntrySummary>
export type TEntryDetail = z.infer<typeof EntryDetail>
export type TExhibitorWithEntries = z.infer<typeof ExhibitorWithEntries>
export type TEntry = z.infer<typeof Entry>
export type TEntryStatus = z.infer<typeof EntryStatus>
export type TEntryCode = z.infer<typeof EntryCode>
export type TIdentifierSummary = z.infer<typeof IdentifierSummary>
export type AllocateIdentifiersInput = z.infer<typeof AllocateIdentifiersRequest>
export type AllocateIdentifiersOutput = z.infer<typeof AllocateIdentifiersResponse>
export type IdentifierAvailabilityOutput = z.infer<typeof IdentifierAvailabilityResponse>
export type TExhibit = z.infer<typeof Exhibit>
export type TScore = z.infer<typeof Score>
export type TAward = z.infer<typeof Award>
export type TAuditEvent = z.infer<typeof AuditEvent>
export type TWpciviExportBundle = z.infer<typeof WpciviExportBundle>
export type THoneyFeedbackBundle = z.infer<typeof HoneyFeedbackBundle>
export type TPerson = z.infer<typeof Person>
export type TShowRoleAssignment = z.infer<typeof ShowRoleAssignment>
export type TShowJudge = z.infer<typeof ShowJudge>
export type ListShowJudgesOutput = z.infer<typeof ShowJudgesResponse>
export type UpdateClassJudgesInput = z.infer<typeof UpdateClassJudgesRequest>
export type TAwardsPolicy = z.infer<typeof AwardsPolicy>
export type TSpecialAwardRecommendation = z.infer<typeof SpecialAwardRecommendation>

export type CreateOrganizationInput = z.infer<typeof CreateOrganizationRequest>
export type CreateShowInput = z.infer<typeof CreateShowRequest>
export type CreateDivisionsInput = z.infer<typeof CreateDivisionsRequest>
export type CreateExhibitorInput = z.infer<typeof CreateExhibitorRequest>
export type UpdateShowExhibitorInput = z.infer<typeof UpdateShowExhibitorRequest>
export type UpdateShowExhibitorOutput = z.infer<typeof UpdateShowExhibitorResponse>
export type UpdateShowEntrantFinalizationInput = z.infer<
  typeof UpdateShowEntrantFinalizationRequest
>
export type UpdateShowEntrantFinalizationOutput = z.infer<
  typeof UpdateShowEntrantFinalizationResponse
>
export type GetShowExhibitorByPersonOutput = z.infer<typeof GetShowExhibitorByPersonResponse>
export type CreateClubInput = z.infer<typeof CreateClubRequest>
export type BulkCreateClubsInput = z.infer<typeof BulkCreateClubsRequest>
export type CreateEntryInput = z.infer<typeof CreateEntryRequest>
export type CheckInInput = z.infer<typeof CheckInRequest>
export type UpdateEntryInput = z.infer<typeof UpdateEntryRequest>
export type UpdateEntryOutput = z.infer<typeof UpdateEntryResponse>
export type JudgingClassMoveInput = z.infer<typeof JudgingClassMoveRequest>
export type JudgingClassMoveOutput = z.infer<typeof JudgingClassMoveResponse>
export type JudgingClassMoveConflictOutput = z.infer<typeof JudgingClassMoveConflict>
export type MarkEntryNotEnteredInput = z.infer<typeof MarkEntryNotEnteredRequest>
export type MarkEntryNotEnteredOutput = z.infer<typeof MarkEntryNotEnteredResponse>
export type GetEntryOutput = z.infer<typeof GetEntryResponse>
export type SubmitScoresInput = z.infer<typeof SubmitScoresRequest>
export type FinalizeAwardsInput = z.infer<typeof FinalizeAwardsRequest>
export type ListOrganizationsOutput = z.infer<typeof ListOrganizationsResponse>
export type UpdateShowInput = z.infer<typeof UpdateShowRequest>
export type UpdateClubInput = z.infer<typeof UpdateClubRequest>
export type BulkCreateClubsOutput = z.infer<typeof BulkCreateClubsResponse>
export type ListShowsOutput = z.infer<typeof ListShowsResponse>
export type TCatalogCategory = z.infer<typeof CatalogCategory>
export type TCatalogClass = z.infer<typeof CatalogClass>
export type TCatalogClassSummary = z.infer<typeof CatalogClassSummary>
export type CatalogRulesRecord = z.infer<typeof CatalogRules>
export type CatalogFlagsRecord = z.infer<typeof CatalogFlags>
export type AddCatalogClassToShowInput = z.infer<typeof AddCatalogClassToShowRequest>
export type ListCatalogCategoriesOutput = z.infer<typeof ListCatalogCategoriesResponse>
export type ListCatalogClassesOutput = z.infer<typeof ListCatalogClassesResponse>
export type LeaderboardOutput = z.infer<typeof LeaderboardResponse>
export type ReportSummaryOutput = z.infer<typeof ReportSummaryResponse>
export type ShowStatsOutput = z.infer<typeof ShowStatsResponse>
export type ShowEditionOutput = z.infer<typeof ShowEdition>
export type ComparisonMetricOutput = z.infer<typeof ComparisonMetric>
export type ClassComparisonRowOutput = z.infer<typeof ClassComparisonRow>
export type ClubComparisonRowOutput = z.infer<typeof ClubComparisonRow>
export type ShowStatsComparisonOutput = z.infer<typeof ShowStatsComparisonResponse>
export type ReportResultsOutput = z.infer<typeof ReportResultsResponse>
export type ReportAwardsOutput = z.infer<typeof ReportAwardsResponse>
export type ReportClubParticipationRowOutput = z.infer<typeof ReportClubParticipationRow>
export type ReportClubParticipationOutput = z.infer<typeof ReportClubParticipationResponse>
export type ReportAwardWinnerOutput = z.infer<typeof ReportAwardWinner>
export type ShowStatsWinnerOutput = z.infer<typeof ShowStatsWinner>
export type ReportBestInDivisionWinnerOutput = z.infer<typeof ReportBestInDivisionWinner>
export type ShowSpecialAwardsOutput = z.infer<typeof ShowSpecialAwardsResponse>
export type SetBestInShowInput = z.infer<typeof SetBestInShowRequest>
export type SetBestInDivisionInput = z.infer<typeof SetBestInDivisionRequest>
export type SetChampionAwardsInput = z.infer<typeof SetChampionAwardsRequest>
export type ReportClubStandingOutput = z.infer<typeof ReportClubStanding>
export type ReportClubAwardOutput = z.infer<typeof ReportClubAward>
export type ReportDivisionResultsOutput = z.infer<typeof ReportDivisionResults>
export type ReportClassResultsOutput = z.infer<typeof ReportClassResults>
export type ReportPlacementEntryOutput = z.infer<typeof ReportPlacementEntry>
export type ReportMemberAwardPlacementOutput = z.infer<typeof ReportMemberAwardPlacement>
export type ReportMemberAwardCardOutput = z.infer<typeof ReportMemberAwardCard>
export type ReportMemberAwardRecipientOutput = z.infer<typeof ReportMemberAwardRecipient>
export type ReportAwardCardsOutput = z.infer<typeof ReportAwardCardsResponse>
export type ReportAwardsEmailPreviewOutput = z.infer<typeof ReportAwardsEmailPreviewResponse>
export type AddCatalogClassToShowOutput = z.infer<typeof AddCatalogClassToShowResponse>
export type CreateCatalogClassInput = z.infer<typeof CreateCatalogClassRequest>
export type UpdateCatalogClassInput = z.infer<typeof UpdateCatalogClassRequest>
export type CatalogClassOutput = z.infer<typeof CatalogClassResponse>
export type UpdateShowClassInput = z.infer<typeof UpdateShowClassRequest>
export type UpdateShowClassOutput = z.infer<typeof UpdateShowClassResponse>
export type UpdateClassFinalizationInput = z.infer<typeof UpdateClassFinalizationRequest>
export type UpdateClassFinalizationOutput = z.infer<typeof UpdateClassFinalizationResponse>
export type CatalogClassUsageInstanceOutput = z.infer<typeof CatalogClassUsageInstance>
export type CatalogClassUsageOutput = z.infer<typeof CatalogClassUsageResponse>
export type ReindexClassCodesInput = z.infer<typeof ReindexClassCodesRequest>
export type ReindexClassCodesOutput = z.infer<typeof ReindexClassCodesResponse>
export type EntrantPacketInput = z.infer<typeof EntrantPacketRequest>
export type ClassPlacementsByEntryCodesInput = z.input<typeof ClassPlacementsByEntryCodesRequest>
export type ClassPlacementsByEntryCodesOutput = z.infer<typeof ClassPlacementsByEntryCodesResponse>
export type TClassSummary = z.infer<typeof ClassSummary>

export interface EntrantPacketDownload {
  buffer: ArrayBuffer
  filename: string | null
  entryCount: number | null
}
export type CreatePersonInput = z.infer<typeof CreatePersonRequest>
export type UpdatePersonInput = z.infer<typeof UpdatePersonRequest>
export type UpdatePersonMembershipInput = z.infer<typeof UpdatePersonMembershipRequest>
export type ListPeopleOutput = z.infer<typeof ListPeopleResponse>
export type MergePeopleInput = z.infer<typeof MergePeopleRequest>
export type MergePeopleOutput = z.infer<typeof MergePeopleResponse>
export type MergePeoplePreviewInput = z.infer<typeof MergePeoplePreviewRequest>
export type MergePeoplePreviewOutput = z.infer<typeof MergePeoplePreviewResponse>
export type TMergeBlockedShow = z.infer<typeof MergeBlockedShow>
export type TPersonParticipationShow = z.infer<typeof PersonParticipationShow>
export type PersonParticipationOutput = z.infer<typeof PersonParticipationResponse>
export type TPersonParticipationEntry = z.infer<typeof PersonParticipationEntry>
export type PersonParticipationEntriesOutput = z.infer<typeof PersonParticipationEntriesResponse>
export type ListExhibitorsOutput = z.infer<typeof ListExhibitorsResponse>
export type ListClubsOutput = z.infer<typeof ListClubsResponse>
export type CreateShowRoleAssignmentInput = z.infer<typeof CreateShowRoleAssignmentRequest>
export type UpdateShowRoleAssignmentInput = z.infer<typeof UpdateShowRoleAssignmentRequest>
export type ListShowRoleAssignmentsOutput = z.infer<typeof ShowRoleAssignmentsResponse>
export type SendReportAwardsEmailInput = z.infer<typeof SendReportAwardsEmailRequest>
export type SendReportAwardsEmailOutput = z.infer<typeof SendReportAwardsEmailResponse>

// Auth types
export type AuthLoginInput = z.infer<typeof AuthLoginRequest>
export type TAuthUser = z.infer<typeof AuthUser>
export type AuthUserResponseOutput = z.infer<typeof AuthUserResponse>
export type TAdminUser = z.infer<typeof AdminUser>
export type ListAdminUsersResponseOutput = z.infer<typeof ListAdminUsersResponse>
export type CreateAdminUserRequestInput = z.infer<typeof CreateAdminUserRequest>
export type CreateAdminUserResponseOutput = z.infer<typeof CreateAdminUserResponse>
export type ChangePasswordRequestInput = z.infer<typeof ChangePasswordRequest>
export type TAccountStatus = z.infer<typeof AccountStatus>
export type TAccessLinkKind = z.infer<typeof AccessLinkKind>
export type AccessLinkInspectOutput = z.infer<typeof AccessLinkInspectResponse>
export type AccessLinkCompleteInput = z.infer<typeof AccessLinkCompleteRequest>
export type AuthOptionsOutput = z.infer<typeof AuthOptionsResponse>
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordRequest>
export type AccessLinkEmailInput = z.infer<typeof AccessLinkEmailRequest>
export type AccessLinkEmailOutput = z.infer<typeof AccessLinkEmailResponse>
export type TIssuedAccessLink = z.infer<typeof IssuedAccessLink>
export type UpdateAdminUserInput = z.infer<typeof UpdateAdminUserRequest>
export type IssueAccessLinkOutput = z.infer<typeof IssueAccessLinkResponse>
export type AdminUserDetailOutput = z.infer<typeof AdminUserDetailResponse>
export type TAccountAuditAction = (typeof ACCOUNT_AUDIT_ACTIONS)[number]
export type TStaffAuditAction = (typeof STAFF_AUDIT_ACTIONS)[number]

export {
  Slug,
  ShowSlug,
  isReservedShowSlug,
  RESERVED_SHOW_SLUGS,
  slugify,
  isValidSlug,
  SLUG_MAX_LENGTH,
  slugPattern,
} from './slug.js'

export {
  ApiClient,
  ApiError,
  apiErrorFromResponse,
  type ApiClientConfig,
  type ApiRequestInit,
} from './generated/api-client.js'

export {
  parseApiErrorMessage,
  parseApiErrorPayload,
  parseApiErrorStatus,
  type ApiErrorPayload,
} from './api-errors.js'

export * from './series-alignment.js'

export * from './series-report.js'
export * from './series-publication.js'
export * from './show-results-release.js'

export {
  WpciviModelCatalogBundle,
  canonicalModelCatalogJson,
  modelCatalogDigestInput,
  type TWpciviModelCatalogBundle,
} from './wpcivi-model-catalog.js'

// Reviewed file exchange. Both producer formats remain unchanged.
import { WpciviModelCatalogBundle as ModelCatalogImportBundle } from './wpcivi-model-catalog.js'

export const CatalogRefreshDecision = z.strictObject({
  sourceKey: z.string().min(1),
  categoryCode: z.string().min(1),
  defaults: z
    .strictObject({
      rules: z.record(z.string(), z.unknown()),
      flags: CatalogFlags.nullable().optional(),
      description: z.string().nullable().optional(),
    })
    .optional(),
  subtypeKeys: z.array(z.string().min(1)).default([]),
  localSubtypes: z.array(z.string().trim().min(1)).default([]),
})
export const CatalogRefreshRequest = z.strictObject({
  bundle: ModelCatalogImportBundle,
  decisions: z.array(CatalogRefreshDecision).default([]),
})
export const CatalogRefreshApplyRequest = CatalogRefreshRequest.extend({
  reviewToken: z.string().min(1),
})
export const CatalogRefreshChange = z.object({
  sourceKey: z.string(),
  name: z.string(),
  kind: z.string(),
  action: z.enum(['add', 'update', 'retire', 'restore', 'unchanged', 'review']),
  modelId: z.string().nullable(),
  before: z.record(z.string(), z.unknown()).nullable(),
  source: z.record(z.string(), z.unknown()),
  proposed: z.record(z.string(), z.unknown()).nullable(),
  references: z.number().int(),
  subtypeReferences: z.number().int(),
  problems: z.array(z.string()),
})
export const CatalogRefreshPreview = z.object({
  reviewToken: z.string(),
  catalogRevision: z.number().int(),
  sourceDigest: z.string(),
  changes: z.array(CatalogRefreshChange),
  warnings: z.array(z.string()),
  conflicts: z.array(z.string()),
  canApply: z.boolean(),
})
export const CatalogRefreshResult = z.object({
  receiptId: z.string(),
  sourceDigest: z.string(),
  catalogRevision: z.number().int(),
  appliedKeys: z.array(z.string()),
  alreadyApplied: z.boolean(),
})
export type TCatalogRefreshRequest = z.infer<typeof CatalogRefreshRequest>
export type TCatalogRefreshDecision = z.infer<typeof CatalogRefreshDecision>
export type TCatalogRefreshPreview = z.infer<typeof CatalogRefreshPreview>

export const WpciviMappingContext = z.strictObject({
  bundleChecksum: z.string().regex(/^[a-f0-9]{64}$/),
  shows: z.array(z.strictObject({ sourceId: WpciviSourceId, name: z.string() })),
  categories: z.array(WpciviShowCategory),
})
export const WpciviClassSelection = z.strictObject({
  categoryIndex: z.number().int().nonnegative(),
  catalogClassId: z.string().min(1),
  catalogSubtypeId: z.string().min(1).nullable(),
})
export const WpciviMappingRequest = z.strictObject({
  context: WpciviMappingContext,
  selections: z.array(WpciviClassSelection),
})
export const WpciviMappingExportRequest = WpciviMappingRequest.extend({
  reviewToken: z.string().min(1),
})
export const WpciviReviewedClassSelection = WpciviClassSelection.extend({
  categoryCode: z.string(),
  modelRevision: z.number().int(),
  subtypeRevision: z.number().int().nullable(),
  modelName: z.string(),
  subtypeName: z.string().nullable(),
})
export const WpciviClassMappingFile = z.strictObject({
  schemaVersion: z.literal('wpcivi-class-mappings.v1'),
  reviewId: z.string(),
  catalogIdentity: z.string(),
  reviewedBy: z.string(),
  reviewedAt: z.string().datetime(),
  context: WpciviMappingContext,
  selections: z.array(WpciviReviewedClassSelection),
})
export const WpciviMappingPreview = z.object({
  reviewToken: z.string(),
  selections: z.array(WpciviReviewedClassSelection),
  issues: z.array(z.string()),
  canExport: z.boolean(),
})
export type TWpciviMappingRequest = z.infer<typeof WpciviMappingRequest>
export type TWpciviClassMappingFile = z.infer<typeof WpciviClassMappingFile>
export type TWpciviMappingPreview = z.infer<typeof WpciviMappingPreview>

/** Same normalized input as the existing importer's SHA-256 checksum. */
export function wpciviBundleChecksumInput(raw: unknown): string {
  return JSON.stringify(WpciviExportBundle.parse(raw))
}
export function wpciviMappingContext(raw: unknown, bundleChecksum: string) {
  const bundle = WpciviExportBundle.parse(raw)
  return WpciviMappingContext.parse({
    bundleChecksum,
    shows: bundle.shows.map(show => ({ sourceId: show.sourceId, name: show.name })),
    categories: bundle.showCategories,
  })
}

export * from './show-package.js'
export * from './show-package-import.js'
export * from './show-handover.js'
export * from './show-delivery.js'
export * from './series-matching.js'

export { classNameKey, classNameWithoutDesignator } from './class-name.js'

export * from './host-state.js'
export * from './public-atlas.js'

export * from './public-classes.js'
export * from './public-championships.js'
export * from './report-years.js'
export * from './public-page-metadata.js'
